<template>
  <div>
    <div class="chart-grid chart-grid-3">
      <div class="chart-card">
        <h3 class="card-title">Findings Status</h3>
        <div v-if="!hasFindingsStatus" class="no-data">No Records Found</div>
        <div v-else class="chart-body">
          <apexchart
            type="donut"
            height="300"
            :options="findingsStatusOptions"
            :series="findingsStatusSeries"
          />
        </div>
      </div>
      <div class="chart-card">
        <h3 class="card-title">Findings by Team</h3>
        <div v-if="!hasDepartmentData" class="no-data">No Records Found</div>
        <div v-else class="chart-body">
          <apexchart
            type="bar"
            height="300"
            :options="departmentChartOptions"
            :series="departmentSeries"
          />
        </div>
      </div>
      <div class="chart-card">
        <h3 class="card-title">Findings by Location</h3>
        <div v-if="!hasLocationData" class="no-data">No Records Found</div>
        <div v-else class="chart-body">
          <apexchart
            type="bar"
            height="300"
            :options="locationChartOptions"
            :series="locationSeries"
          />
        </div>
      </div>
    </div>
    <div class="chart-grid chart-grid-3">
      <div class="chart-card">
        <h3 class="card-title">CAPA Status</h3>
        <div v-if="!hasCapaStatus" class="no-data">No Records Found</div>
        <div v-else class="chart-body">
          <apexchart
            type="donut"
            height="300"
            :options="capaStatusOptions"
            :series="capaStatusSeries"
          />
        </div>
      </div>
      <div class="chart-card">
        <h3 class="card-title">CAPA Status by Team</h3>
        <div v-if="!hasCapaTeamData" class="no-data">No Records Found</div>
        <div v-else class="chart-body">
          <apexchart
            type="bar"
            height="300"
            :options="capaTeamChartOptions"
            :series="capaTeamSeries"
          />
        </div>
      </div>
      <div class="chart-card">
        <h3 class="card-title">CAPA Status by Location</h3>
        <div v-if="!hasCapaLocationData" class="no-data">No Records Found</div>
        <div v-else class="chart-body">
          <apexchart
            type="bar"
            height="300"
            :options="capaLocationChartOptions"
            :series="capaLocationSeries"
          />
        </div>
      </div>
    </div>
    <div class="chart-grid chart-grid-single">
      <div class="chart-card chart-card-wide">
        <h3 class="card-title">CAPA Overdue by Team</h3>
        <div v-if="!hasCapaOverdueData" class="no-data">No overdue CAPA tasks</div>
        <div v-else class="chart-body">
          <apexchart
            type="radar"
            height="340"
            :options="capaOverdueChartOptions"
            :series="capaOverdueSeries"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { CAPA_STATUS_ORDER, FINDING_STATUS_ORDER, rollupLocations, stackedStatusSeries } from "../../utils/dashboardChartData";
import { donutOptions, radarOptions, stackedBarOptions } from "../../utils/dashboardTheme";

export default defineComponent({
  name: "FindingsSection",
  props: {
    findings: { type: Array, default: () => [] },
    teamFindings: { type: Array, default: () => [] },
    capaTasks: { type: Array, default: () => [] },
    capaTeamTasks: { type: Array, default: () => [] },
    capaOverdueByTeam: { type: Array, default: () => [] },
  },
  data(): any {
    return {};
  },
  computed: {
    findingsStatusSeries(): number[] {
      return ((this.findings as any[]) || []).map((item: any) => item.total || 0);
    },
    hasFindingsStatus(): boolean {
      return this.findingsStatusSeries.some((value: number) => Number(value) > 0);
    },
    findingsStatusKey(): string {
      return this.findingsStatusSeries.join("-");
    },
    findingsStatusOptions(): Record<string, any> {
      const labels = ((this.findings as any[]) || []).map((item: any) => item.label || "N/A");
      return donutOptions(labels);
    },
    capaStatusSeries(): number[] {
      return ((this.capaTasks as any[]) || []).map((item: any) => item.total || 0);
    },
    hasCapaStatus(): boolean {
      return this.capaStatusSeries.some((value: number) => Number(value) > 0);
    },
    capaStatusKey(): string {
      return this.capaStatusSeries.join("-");
    },
    capaStatusOptions(): Record<string, any> {
      const labels = ((this.capaTasks as any[]) || []).map((item: any) => item.label || "N/A");
      return donutOptions(labels);
    },
    departmentsFlat(): any[] {
      return ((this.teamFindings as any[]) || [])
        .flatMap((location: any) => location.teams || location.Teams || [])
        .filter((team: any) =>
          (Number(team.totalTasks) || 0) > 0 ||
          ((team.status || []) as any[]).some((item: any) => (Number(item.total) || 0) > 0)
        )
        .map((team: any) => ({
          ...team,
          teamName: team.teamName || team.team,
        }));
    },
    hasDepartmentData(): boolean {
      return this.departmentsFlat.length > 0;
    },
    departmentKey(): string {
      return this.departmentsFlat.map((team: any) => team.teamName).join("-");
    },
    departmentSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.departmentsFlat, FINDING_STATUS_ORDER);
    },
    departmentChartOptions(): Record<string, any> {
      const teams: any[] = this.departmentsFlat;
      return stackedBarOptions(
        teams.map((team: any) => team.teamName),
        this.departmentSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    locationRows(): any[] {
      return rollupLocations(this.teamFindings as any[], FINDING_STATUS_ORDER);
    },
    hasLocationData(): boolean {
      return this.locationRows.length > 0;
    },
    locationKey(): string {
      return this.locationRows.map((location: any) => location.locationName).join("-");
    },
    locationSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.locationRows, FINDING_STATUS_ORDER);
    },
    locationChartOptions(): Record<string, any> {
      return stackedBarOptions(
        this.locationRows.map((location: any) => location.locationName),
        this.locationSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    capaTeamsFlat(): any[] {
      return ((this.capaTeamTasks as any[]) || [])
        .flatMap((location: any) => location.teams || location.Teams || [])
        .filter((team: any) =>
          (Number(team.totalTasks) || 0) > 0 ||
          ((team.status || []) as any[]).some((item: any) => (Number(item.total) || 0) > 0)
        )
        .map((team: any) => ({
          ...team,
          teamName: team.teamName || team.team,
        }));
    },
    hasCapaTeamData(): boolean {
      return this.capaTeamsFlat.length > 0;
    },
    capaTeamKey(): string {
      return this.capaTeamsFlat.map((team: any) => team.teamName || team.team).join("-");
    },
    capaTeamSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.capaTeamsFlat, CAPA_STATUS_ORDER);
    },
    capaTeamChartOptions(): Record<string, any> {
      return stackedBarOptions(
        this.capaTeamsFlat.map((team: any) => team.teamName || team.team),
        this.capaTeamSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    capaLocationRows(): any[] {
      return rollupLocations(this.capaTeamTasks as any[], CAPA_STATUS_ORDER);
    },
    hasCapaLocationData(): boolean {
      return this.capaLocationRows.length > 0;
    },
    capaLocationKey(): string {
      return this.capaLocationRows.map((location: any) => location.locationName).join("-");
    },
    capaLocationSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(this.capaLocationRows, CAPA_STATUS_ORDER);
    },
    capaLocationChartOptions(): Record<string, any> {
      return stackedBarOptions(
        this.capaLocationRows.map((location: any) => location.locationName),
        this.capaLocationSeries.map((series: { name: string; data: number[] }) => series.name)
      );
    },
    capaOverdueTeams(): any[] {
      return ((this.capaOverdueByTeam as any[]) || []).filter((team: any) => (Number(team.total) || 0) > 0);
    },
    hasCapaOverdueData(): boolean {
      return this.capaOverdueTeams.length > 0;
    },
    capaOverdueKey(): string {
      return this.capaOverdueTeams.map((team: any) => `${team.teamName}-${team.total}`).join("-");
    },
    capaOverdueSeries(): { name: string; data: number[] }[] {
      return [
        {
          name: "Overdue CAPA",
          data: this.capaOverdueTeams.map((team: any) => Number(team.total) || 0),
        },
      ];
    },
    capaOverdueChartOptions(): Record<string, any> {
      return radarOptions(this.capaOverdueTeams.map((team: any) => team.teamName || "Unknown"));
    },
  },
});
</script>
